import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { OutageViewModel } from "../../../../api/generated/models";
import { RootState } from "../../../../state-management/root-reducer";
import { ISearchedOutages } from "../../../../state-management/slices/outages/outages.slice";
import FormStyles from "../../Form.styles";
import OutageSearchStyles from "../OutageSearch.styles";
import { IOutageSearchResultsProps } from "../../Form.types";
import STREETLIGHT_ICON from "../../../icon/streetlight-icon/StreetlightIcon.component";
import { BracketTypeDisplayMap } from "../../../../shared/enums/utility.enums";
import {
  getKeyByValue,
  isNullOrUndefined,
} from "../../../../shared/utils/data.utils";
import { getMarkerStatusFlag } from "../../../../state-management/utils/state/outages.utils";
import { Image } from "react-bootstrap";
import ExclamationIcon from "../../../../theme/assets/exclamation.svg";
import ButtonStyles from "../../../button/Button.styles";
import BUTTON from "../../../button/Button.component";
import { AppDispatch } from "../../../../state-management/store";
import {
  clearSearchTerm,
  setSearchView,
} from "../../../../state-management/slices/global/global.slice";
import { ESearchWizardSteps } from "../../../../shared/enums/search.enums";
import { ClickEvent } from "../../../../shared/types/event.types";
import { EMarkerStatuses } from "../../../map/Map.constants";
import ColorsPalette from "../../../../theme/colors/Colors.palette";

const renderNotificationId = (outage: OutageViewModel): string => {
  if (
    !outage.sapNotification ||
    outage.sapNotification === undefined ||
    outage.sapNotification === ""
  ) {
    return "Pending";
  }

  return `#${outage.sapNotification}`;
};

const getStatusClassNames = (
  reportedStatus: number,
  markerStatus: string | null | undefined
): string | undefined => {
  if (markerStatus === EMarkerStatuses.EXTENDED_REPAIR) {
    return "status extended-repair";
  }

  if (reportedStatus) {
    return "status reported";
  } else {
    return "status non-reported";
  }
};

const getMarkerStatusLabel = (markerStatus: string | null | undefined) => {
  if (isNullOrUndefined(markerStatus)) {
    throw new Error("markerStatus must be defined - getMarkerStatusLabel");
  }

  if (
    markerStatus === EMarkerStatuses.REPORTED ||
    markerStatus === EMarkerStatuses.IN_PROGRESS
  ) {
    return "In Progress";
  } else {
    return markerStatus;
  }
};

const renderResults = (outages: Array<OutageViewModel>) => {
  return outages.map((outage, index) => {
    const { utility, markerStatus } = outage;
    const bracketType = utility?.bracketType;
    const reportedStatus = getMarkerStatusFlag(markerStatus);

    const reportedDateString = new Date(outage.reportedDateTime as string);
    const reportedDate = new Intl.DateTimeFormat("en-US", {
      dateStyle: "long",
    }).format(reportedDateString);

    const streetlightType = getKeyByValue(
      BracketTypeDisplayMap,
      bracketType as string
    );

    return (
      <OutageSearchStyles.ResultsListItem
        className="results-list-item"
        key={`outage-${outage.outageId}`}
      >
        <OutageSearchStyles.ResultsContainer className="results-container">
          {markerStatus !== EMarkerStatuses.EXTENDED_REPAIR && (
            <STREETLIGHT_ICON
              type={streetlightType}
              reported={reportedStatus}
            />
          )}

          {markerStatus === EMarkerStatuses.EXTENDED_REPAIR && (
            <STREETLIGHT_ICON
              type={streetlightType}
              reported={reportedStatus}
              bgFill={ColorsPalette.MapMarkers.extendedRepairStroke}
            />
          )}

          <OutageSearchStyles.ResultsContent className="results-content">
            <div className="results-header">
              <div
                className={getStatusClassNames(reportedStatus, markerStatus)}
              >
                {getMarkerStatusLabel(markerStatus)}
              </div>
              <div className="reported-date">{`Reported ${reportedDate}`}</div>
            </div>

            <p className="address">
              {outage.location?.address} {outage.location?.city} MI
            </p>
            <p className="notification-id">{renderNotificationId(outage)}</p>
          </OutageSearchStyles.ResultsContent>
        </OutageSearchStyles.ResultsContainer>
      </OutageSearchStyles.ResultsListItem>
    );
  });
};

const renderNoResults = (
  searchTerm: string,
  dispatch: AppDispatch,
  onClose: (event: ClickEvent) => void
) => {
  return (
    <OutageSearchStyles.NoResultsContainer className="no-results-container">
      <div className="icon-container">
        <Image src={ExclamationIcon} />
      </div>
      <p>Sorry, we couldn&#39;t find any reports for {`"${searchTerm}"`} </p>

      <ButtonStyles.ButtonContainer className="button-container">
        <BUTTON
          className="submit-outage-button"
          variant="primary"
          type="button"
          onClick={(event: ClickEvent) => {
            dispatch(clearSearchTerm());
            dispatch(setSearchView({ activeView: ESearchWizardSteps.SEARCH }));
          }}
        >
          Try Again
        </BUTTON>
      </ButtonStyles.ButtonContainer>
      <ButtonStyles.ButtonContainer className="button-container">
        <ButtonStyles.LinkButton
          className="link-button"
          title="Back to Outage Map"
          type="button"
          onClick={(event) => {
            onClose(event);
          }}
        >
          Back to Outage Map
        </ButtonStyles.LinkButton>
      </ButtonStyles.ButtonContainer>
    </OutageSearchStyles.NoResultsContainer>
  );
};

const OUTAGE_SEARCH_RESULTS: React.FC<IOutageSearchResultsProps> = (
  props: IOutageSearchResultsProps
) => {
  // hooks
  const dispatch = useDispatch();
  // props
  const { onClose } = props;

  // App State
  const { searchedOutages } = useSelector((state: RootState) => state.outages);
  const { outageCollection } = searchedOutages;
  const { searchMode } = useSelector(
    (state: RootState) => state.globalState.ui
  );

  return (
    <FormStyles.FormContainer
      className="form-container search-results force-scroll"
      data-testid="outage-search-form-results"
    >
      {outageCollection && outageCollection.length && (
        <>
          <OutageSearchStyles.SearchResultsList className="search-results-list">
            {renderResults(outageCollection)}
          </OutageSearchStyles.SearchResultsList>
          <ButtonStyles.ButtonContainer className="button-container">
            <ButtonStyles.LinkButton
              className="link-button"
              title="Back to Outage Map"
              type="button"
              onClick={(event) => {
                onClose(event);
              }}
            >
              Back to Outage Map
            </ButtonStyles.LinkButton>
          </ButtonStyles.ButtonContainer>
        </>
      )}
      {(!outageCollection || !outageCollection.length) &&
        renderNoResults(searchMode.searchText || "", dispatch, onClose)}
    </FormStyles.FormContainer>
  );
};
export default OUTAGE_SEARCH_RESULTS;
